import { OrderBook } from "../../Interfaces/orderInterfaces";
import { useEffect, useRef, useState } from "react";

const WS_HIGHLIGHT_DURATION_MS = 500;

export const ORDER_BOOK_CELL_FIELDS = [
  "zOrdMeDem",
  "qTitMeDem",
  "pMeDem",
  "pMeOf",
  "qTitMeOf",
  "zOrdMeOf",
] as const;

export type OrderBookCellField = (typeof ORDER_BOOK_CELL_FIELDS)[number];

export const getOrderBookCellKey = (
  rowIndex: number,
  field: OrderBookCellField,
) => `${rowIndex}:${field}`;

export const getChangedOrderBookCellKeys = (
  previous?: OrderBook,
  current?: OrderBook,
): string[] => {
  if (!previous || !current) return [];

  const previousRows = previous.bestLimitsDto?.bestLimits || [];
  const currentRows = current.bestLimitsDto?.bestLimits || [];
  const rowCount = Math.max(previousRows.length, currentRows.length);
  const changedCells: string[] = [];

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex++)
    for (const field of ORDER_BOOK_CELL_FIELDS)
      if (previousRows[rowIndex]?.[field] !== currentRows[rowIndex]?.[field])
        changedCells.push(getOrderBookCellKey(rowIndex, field));

  return changedCells;
};

export const useOrderBookCellHighlights = (tableInfo?: OrderBook) => {
  const [highlightedCells, setHighlightedCells] = useState<Set<string>>(
    () => new Set(),
  );
  const previousTableInfoRef = useRef<OrderBook>();
  const highlightTimersRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());

  useEffect(() => {
    const previousTableInfo = previousTableInfoRef.current;
    previousTableInfoRef.current = tableInfo;

    if (!tableInfo?.wsUpdateSequence) return;

    const changedCells = getChangedOrderBookCellKeys(
      previousTableInfo,
      tableInfo,
    );
    if (!changedCells.length) return;

    setHighlightedCells((current) => {
      const next = new Set(current);
      changedCells.forEach((cell) => next.add(cell));
      return next;
    });

    changedCells.forEach((cell) => {
      const currentTimer = highlightTimersRef.current.get(cell);
      if (currentTimer) clearTimeout(currentTimer);

      const timer = setTimeout(() => {
        highlightTimersRef.current.delete(cell);
        setHighlightedCells((current) => {
          if (!current.has(cell)) return current;
          const next = new Set(current);
          next.delete(cell);
          return next;
        });
      }, WS_HIGHLIGHT_DURATION_MS);
      highlightTimersRef.current.set(cell, timer);
    });
  }, [tableInfo]);

  useEffect(
    () => () => {
      highlightTimersRef.current.forEach(clearTimeout);
      highlightTimersRef.current.clear();
    },
    [],
  );

  return highlightedCells;
};
